import {
  Component,
  Inject,
  Input,
  OnInit,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { PlanoAlimentarService } from '../../../../../services/plano-alimentar/plano-alimentar.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TacoAlimento } from '../../../../../models/TacoAlimento';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { TacoAlimentoAg } from '../../../../../models/TacoAlimentoAg';
import { TacoAlimentoAmino } from '../../../../../models/TacoAlimentoAmino';

interface NutrienteAlimento {
  nutriente: string;
  quantidade: string;
}

@Component({
  selector: 'app-detalhes-alimento-taco',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatTableModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
  ],
  templateUrl: './detalhes-alimento-taco.html',
  styleUrl: './detalhes-alimento-taco.scss',
})
export class DetalhesAlimentoTaco implements OnInit, AfterViewInit {
  @Input() idAlimento!: number;
  @Input() gramas!: number | undefined;

  @ViewChild('sort1') sort1!: MatSort;
  @ViewChild('sort2') sort2!: MatSort;

  isLoading: boolean = false;

  displayedColumns: string[] = ['nutriente', 'quantidade'];

  nutrientesAlimento1 = new MatTableDataSource<NutrienteAlimento>([]);
  nutrientesAlimento2 = new MatTableDataSource<NutrienteAlimento>([]);

  hasData1: boolean = false;
  hasData2: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<DetalhesAlimentoTaco>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private planoAlimentarService: PlanoAlimentarService
  ) {
    this.idAlimento = data.idAlimento;
    this.gramas = data.gramas || 100;
  }

  ngAfterViewInit(): void {
    this.initializeSort();
  }

  initializeSort(): void {
    if (this.sort1) {
      this.nutrientesAlimento1.sort = this.sort1;
    }
    if (this.sort2) {
      this.nutrientesAlimento2.sort = this.sort2;
    }

    this.configureCustomSort();
  }

  configureCustomSort(): void {
    this.nutrientesAlimento1.sortingDataAccessor = (item, property) => {
      return this.getSortValue(item, property);
    };

    this.nutrientesAlimento2.sortingDataAccessor = (item, property) => {
      return this.getSortValue(item, property);
    };
  }

  private getSortValue(item: NutrienteAlimento, property: string): any {
    switch (property) {
      case 'nutriente':
        return item.nutriente;
      case 'quantidade':
        const match = item.quantidade.match(/(\d+\.?\d*)/);
        return match ? parseFloat(match[1]) : item.quantidade;
      default:
        return item[property as keyof NutrienteAlimento];
    }
  }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    try {
      const alimento = await this.planoAlimentarService.detalharAlimentoTaco(
        this.idAlimento
      );

      const data1 = this.tacoToArray(alimento, 1);
      const data2 = this.tacoToArray(alimento, 2);

      this.nutrientesAlimento1.data = data1;
      this.nutrientesAlimento2.data = data2;

      this.hasData1 = data1.length > 0;
      this.hasData2 = data2.length > 0;

      setTimeout(() => {
        this.initializeSort();
      });
    } catch (err) {
      console.error('Erro ao detalhar alimento TACO:', err);
    } finally {
      this.isLoading = false;
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  tacoToArray(alimento: TacoAlimento, kind: 1 | 2): NutrienteAlimento[] {
    const formatValue = (value: any, unit: string) => {
      return value != null ? `${value} ${unit}` : 'N/A';
    };

    const toNumber = (value: any): number => {
      if (value == null) return 0;
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const numStr = value.replace(/[^\d,.-]/g, '').replace(',', '.');
        return parseFloat(numStr) || 0;
      }
      return 0;
    };

    const calcularProporcao = (valor: any, unit: string): string => {
      const valorNumerico = toNumber(valor);
      if (valorNumerico === 0) return 'N/A';

      const gramas = this.gramas || 100;
      const valorProporcional = (valorNumerico * gramas) / 100;

      return formatValue(this.arredondarValor(valorProporcional), unit);
    };

    const getAgValue = (property: keyof TacoAlimentoAg): any => {
      if (!alimento.ag || alimento.ag.length === 0) return null;
      return alimento.ag[0][property];
    };

    const getAminoValue = (property: keyof TacoAlimentoAmino): any => {
      if (!alimento.amino || alimento.amino.length === 0) return null;
      return alimento.amino[0][property];
    };

    switch (kind) {
      case 1:
        return [
          {
            nutriente: 'Umidade',
            quantidade: formatValue(alimento.umidadePercent, '%'),
          },
          {
            nutriente: 'Energia',
            quantidade: calcularProporcao(alimento.energiaKcal, 'kcal'),
          },
          {
            nutriente: 'Energia (kJ)',
            quantidade: calcularProporcao(alimento.energiaKj, 'kJ'),
          },
          {
            nutriente: 'Proteína',
            quantidade: calcularProporcao(alimento.proteinaG, 'g'),
          },
          {
            nutriente: 'Lipídios',
            quantidade: calcularProporcao(alimento.lipidiosG, 'g'),
          },
          {
            nutriente: 'Colesterol',
            quantidade: calcularProporcao(alimento.colesterolMg, 'mg'),
          },
          {
            nutriente: 'Carboidrato',
            quantidade: calcularProporcao(alimento.carboidratoG, 'g'),
          },
          {
            nutriente: 'Fibra Alimentar',
            quantidade: calcularProporcao(alimento.fibraG, 'g'),
          },
          {
            nutriente: 'Cinzas',
            quantidade: calcularProporcao(alimento.cinzasG, 'g'),
          },
          {
            nutriente: 'Cálcio',
            quantidade: calcularProporcao(alimento.calcioMg, 'mg'),
          },
          {
            nutriente: 'Magnésio',
            quantidade: calcularProporcao(alimento.magnesioMg, 'mg'),
          },
          {
            nutriente: 'Manganês',
            quantidade: calcularProporcao(alimento.manganesMg, 'mg'),
          },
          {
            nutriente: 'Fósforo',
            quantidade: calcularProporcao(alimento.fosforoMg, 'mg'),
          },
          {
            nutriente: 'Ferro',
            quantidade: calcularProporcao(alimento.ferroMg, 'mg'),
          },
          {
            nutriente: 'Sódio',
            quantidade: calcularProporcao(alimento.sodioMg, 'mg'),
          },
          {
            nutriente: 'Potássio',
            quantidade: calcularProporcao(alimento.potassioMg, 'mg'),
          },
          {
            nutriente: 'Cobre',
            quantidade: calcularProporcao(alimento.cobreMg, 'mg'),
          },
          {
            nutriente: 'Zinco',
            quantidade: calcularProporcao(alimento.zincoMg, 'mg'),
          },
          {
            nutriente: 'Retinol',
            quantidade: calcularProporcao(alimento.retinolMcg, 'mcg'),
          },
          {
            nutriente: 'RE',
            quantidade: calcularProporcao(alimento.reMcg, 'mcg'),
          },
          {
            nutriente: 'RAE',
            quantidade: calcularProporcao(alimento.raeMcg, 'mcg'),
          },
          {
            nutriente: 'Tiamina',
            quantidade: calcularProporcao(alimento.tiaminaMg, 'mg'),
          },
          {
            nutriente: 'Riboflavina',
            quantidade: calcularProporcao(alimento.riboflavinaMg, 'mg'),
          },
          {
            nutriente: 'Piridoxina',
            quantidade: calcularProporcao(alimento.piridoxinaMg, 'mg'),
          },
          {
            nutriente: 'Niacina',
            quantidade: calcularProporcao(alimento.niacinaMg, 'mg'),
          },
          {
            nutriente: 'Vitamina C',
            quantidade: calcularProporcao(alimento.vitaminaCMg, 'mg'),
          },

          {
            nutriente: 'Saturados',
            quantidade: calcularProporcao(getAgValue('saturadosG'), 'g'),
          },
          {
            nutriente: 'Monoinsaturados',
            quantidade: calcularProporcao(getAgValue('monoinsaturadosG'), 'g'),
          },
          {
            nutriente: 'Poliinsaturados',
            quantidade: calcularProporcao(getAgValue('poliinsaturadosG'), 'g'),
          },

          {
            nutriente: 'Triptofano',
            quantidade: calcularProporcao(getAminoValue('triptofanoG'), 'g'),
          },
          {
            nutriente: 'Treonina',
            quantidade: calcularProporcao(getAminoValue('treoninaG'), 'g'),
          },
          {
            nutriente: 'Isoleucina',
            quantidade: calcularProporcao(getAminoValue('isoleucinaG'), 'g'),
          },
          {
            nutriente: 'Leucina',
            quantidade: calcularProporcao(getAminoValue('leucinaG'), 'g'),
          },
          {
            nutriente: 'Lisina',
            quantidade: calcularProporcao(getAminoValue('lisinaG'), 'g'),
          },
          {
            nutriente: 'Metionina',
            quantidade: calcularProporcao(getAminoValue('metioninaG'), 'g'),
          },
          {
            nutriente: 'Cistina',
            quantidade: calcularProporcao(getAminoValue('cistinaG'), 'g'),
          },
          {
            nutriente: 'Fenilalanina',
            quantidade: calcularProporcao(getAminoValue('fenilalaninaG'), 'g'),
          },
          {
            nutriente: 'Tirosina',
            quantidade: calcularProporcao(getAminoValue('tirosinaG'), 'g'),
          },
          {
            nutriente: 'Valina',
            quantidade: calcularProporcao(getAminoValue('valinaG'), 'g'),
          },
          {
            nutriente: 'Arginina',
            quantidade: calcularProporcao(getAminoValue('argininaG'), 'g'),
          },
          {
            nutriente: 'Histidina',
            quantidade: calcularProporcao(getAminoValue('histidinaG'), 'g'),
          },
          {
            nutriente: 'Alanina',
            quantidade: calcularProporcao(getAminoValue('alaninaG'), 'g'),
          },
          {
            nutriente: 'Ácido Aspártico',
            quantidade: calcularProporcao(
              getAminoValue('acidoAsparticoG'),
              'g'
            ),
          },
          {
            nutriente: 'Ácido Glutâmico',
            quantidade: calcularProporcao(
              getAminoValue('acidoGlutamicoG'),
              'g'
            ),
          },
          {
            nutriente: 'Glicina',
            quantidade: calcularProporcao(getAminoValue('glicinaG'), 'g'),
          },
          {
            nutriente: 'Prolina',
            quantidade: calcularProporcao(getAminoValue('prolinaG'), 'g'),
          },
          {
            nutriente: 'Serina',
            quantidade: calcularProporcao(getAminoValue('serinaG'), 'g'),
          },
        ].filter((item) => item.quantidade !== 'N/A');
      default:
        return [];
    }
  }

  private arredondarValor(valor: number, casasDecimais: number = 2): number {
    const fator = Math.pow(10, casasDecimais);
    return Math.round(valor * fator) / fator;
  }
}
